import { type PlanAttributes, PlanStatus } from '../dm/Plan.js';

/**
 * Whether a plan should appear in the list of available plans; deleted
 * and superseded plans are only kept (by sync) for users who are
 * subscribed to them
 */
export const isPlanListable = (plan: Pick<PlanAttributes, 'status'>): boolean =>
  plan.status === PlanStatus.Saved || plan.status === PlanStatus.Published;

export interface PlanPermissions {
  /**
   * Full edit (days, settings); only unpublished plans
   */
  canEdit: boolean;
  canEditName: boolean;
  canNewVersion: boolean;
  canDelete: boolean;
  canSubscribe: boolean;
}

/**
 * What a user can do with a plan. Mirrors the API's rules, and assumes
 * the plan is one the user can see: a non-admin plan is only ever
 * visible to its owner, so "owns it" reduces to `!plan.isAdmin`, while
 * admin plans can be changed by any admin.
 */
export const getPlanPermissions = (
  plan: Pick<PlanAttributes, 'isAdmin' | 'status'>,
  user: { isAdmin: boolean }
): PlanPermissions => {
  const canChange = !plan.isAdmin || user.isAdmin;
  const { status } = plan;

  return {
    canEdit: canChange && status === PlanStatus.Saved,
    canEditName: canChange && (status === PlanStatus.Saved || status === PlanStatus.Published),
    canNewVersion: canChange && status === PlanStatus.Published,
    canDelete: canChange && status !== PlanStatus.Deleted,
    canSubscribe: status === PlanStatus.Published,
  };
};
